import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import { DateTimeText, Id, Mono } from '../../components/ui/Mono';
import Select from '../../components/ui/Select';
import { SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import LpnBatchModal from '../../components/warehouse/LpnBatchModal';
import LpnDetailPanel, { LpnStatusBadgeVariant } from '../../components/warehouse/LpnDetailPanel';
import { LocationPicker, ProductPicker, useActiveLocations } from '../../components/warehouse/lpnPickers';
import { ExtractListRows } from '../../components/warehouse/payload';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import { ExtractPaging } from '../../utils/aggregation';
import { FormatInt } from '../../utils/format';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const LPN_STATUSES = ['ACTIVE', 'CLOSED'];

const BLANK_CELL = <span className="text-mono-400">—</span>;

/**
 * /warehouse/lpns — LPNs & Recebimento (M3).
 *
 * O LPN é a unidade que o armazém movimenta: o saldo vive nos ITENS dele
 * (PRD §3) e o código `{PREFIX}-{YYMMDD}-{SEQ}` é o que a etiqueta carrega. A
 * página responde as duas perguntas do dia a dia — "onde está o LPN X?" e "o
 * que tem nesta posição?" — e recebe mercadoria (criação + etiqueta na hora).
 *
 * Layout espelha a página de Localizações do M1: listagem à esquerda, ficha
 * PERMANENTE à direita (clicar na linha preenche a ficha, sem modal). Busca,
 * filtros e paginação são do SERVIDOR (wire snake_case: code, status,
 * location_id, product_id, limit, offset) — nada de filtrar no cliente uma
 * lista que veio paginada.
 */
const WarehouseLpnsPage = () => {
    const { label } = useWarehouseLabels();
    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [codeInput, setCodeInput] = useState('');
    const [status, setStatus] = useState('');
    const [locationId, setLocationId] = useState('');
    const [product, setProduct] = useState(null);
    const [selectedCode, setSelectedCode] = useState(null);
    // M10 r2 — a porta única "Imprimir LPNs" (rolo em branco OU já com origem/itens).
    const [isBatchOpen, setBatchOpen] = useState(false);

    const debouncedCode = useDebounce(codeInput, 300);
    const locations = useActiveLocations(true);
    const productId = product?.product_id ?? '';

    const loadLpns = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const params = { limit: pageSize, offset };
            const code = debouncedCode.trim();
            if (code) {
                params.code = code;
            }
            if (status) {
                params.status = status;
            }
            if (locationId) {
                params.location_id = locationId;
            }
            if (productId) {
                params.product_id = productId;
            }
            const data = await api.getWarehouseLpns(params);
            const list = ExtractListRows(data, ['lpns']);
            setRows(list);
            setPaging(ExtractPaging(data, { total: list.length, limit: pageSize, offset }));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [page, pageSize, debouncedCode, status, locationId, productId]);

    useEffect(() => {
        loadLpns();
    }, [loadLpns]);

    // Filtro novo sempre volta para a primeira página (a StandardTable já faz
    // isso pela busca; os selects deste cabeçalho são nossos).
    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.field.code'),
                sortable: false,
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'status',
                label: label('wms.field.status'),
                sortable: false,
                getValue: (row) => label(`wms.lpn_status.${row.status}`),
                render: (row) => (
                    <Badge variant={LpnStatusBadgeVariant(row.status)}>{label(`wms.lpn_status.${row.status}`)}</Badge>
                ),
            },
            {
                // Clicar na POSIÇÃO filtra a lista por ela ("o que mais tem
                // aqui?") — a alternativa era um código clicável que não faz
                // nada, e controle morto é pior que nenhum controle.
                key: 'location_code',
                label: label('wms.lpn.field.location'),
                sortable: false,
                getValue: (row) => row.location_code || '',
                render: (row) =>
                    row.location_code ? (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                setLocationId(String(row.location_id ?? ''));
                                setPage(1);
                            }}
                            title={label('wms.lpns.filter_by_location')}
                            className="rounded text-link-600 hover:underline dark:text-link-400"
                        >
                            <Mono>{row.location_code}</Mono>
                        </button>
                    ) : (
                        BLANK_CELL
                    ),
            },
            {
                key: 'deposit_code',
                label: label('wms.lpn.field.deposit'),
                sortable: false,
                getValue: (row) => row.deposit_code || '',
                render: (row) => (row.deposit_code ? <Mono>{row.deposit_code}</Mono> : BLANK_CELL),
            },
            {
                key: 'item_count',
                label: label('wms.lpn.field.item_count'),
                sortable: false,
                align: 'right',
                getValue: (row) => row.item_count ?? 0,
                render: (row) => <Mono>{FormatInt(row.item_count ?? 0)}</Mono>,
            },
            {
                key: 'is_bulky',
                label: label('wms.lpn.field.is_bulky'),
                sortable: false,
                align: 'center',
                getValue: (row) => (row.is_bulky ? 'Sim' : 'Não'),
                render: (row) => (row.is_bulky ? <Badge variant="warning">Sim</Badge> : BLANK_CELL),
            },
            {
                key: 'created_at',
                label: label('wms.lpn.field.created_at'),
                sortable: false,
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
        ],
        [label],
    );

    const filtersCard = (
        <div className="grid grid-cols-1 gap-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950 md:grid-cols-3">
            <Select
                label={label('wms.field.status')}
                value={status}
                onChange={(event) => changeFilter(setStatus)(event.target.value)}
            >
                <option value="">{label('wms.lpns.status_all')}</option>
                {LPN_STATUSES.map((option) => (
                    <option key={option} value={option}>
                        {label(`wms.lpn_status.${option}`)}
                    </option>
                ))}
            </Select>
            <LocationPicker
                label={label}
                locations={locations.rows}
                loading={locations.loading}
                value={locationId}
                onChange={changeFilter(setLocationId)}
                emptyOptionText={label('wms.lpns.location_all')}
            />
            <ProductPicker
                label={label}
                fieldLabel={label('wms.lpns.product_filter')}
                value={product}
                onChange={changeFilter(setProduct)}
            />
        </div>
    );

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={7} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.lpns.title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={loadLpns}
        />
    ) : (
        // Busca controlada dentro da tabela (o input não pode ser desmontado a
        // cada refetch, senão a digitação perde o foco).
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || row.code || index}
            onRowClick={(row) => setSelectedCode(row.code)}
            countLabel={label('wms.lpns.count')}
            exportFileName="wms-lpns"
            emptyMessage={label('wms.lpns.empty')}
            searchPlaceholder={label('wms.lpns.search_placeholder')}
            searchMode="server"
            searchTerm={codeInput}
            onSearchTermChange={setCodeInput}
            sortMode="server"
            paginationMode="server"
            totalItems={paging.total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title={label('wms.lpns.title')}
                subtitle={label('wms.lpns.description')}
                actions={
                    <div className="flex gap-2">
                        <Button onClick={() => setBatchOpen(true)}>
                            <Tag size={18} className="mr-2" />
                            {label('wms.lpn_batch.action')}
                        </Button>
                    </div>
                }
            />

            {filtersCard}

            <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
                <div className="min-w-0 flex-1">{listContent}</div>
                <aside className="w-full shrink-0 xl:w-[360px]">
                    <div className="h-full xl:sticky xl:top-4">
                        <LpnDetailPanel code={selectedCode} label={label} onClear={() => setSelectedCode(null)} />
                    </div>
                </aside>
            </div>

            <LpnBatchModal
                isOpen={isBatchOpen}
                onClose={() => setBatchOpen(false)}
                onCreated={() => {
                    setPage(1);
                    loadLpns();
                }}
            />
        </div>
    );
};

export default WarehouseLpnsPage;
